// Pure derive helpers for Weekly/Monthly review (D30, P2-03).
// All functions are deterministic given inputs; no side effects.
// TIMEZONE: never use toISOString().slice — use date.ts helpers for local dates.

import { addDaysIso } from "../lib/date";
import { monthLedger } from "./bento-derive";
import type { LifeOsState } from "../types";
import type { ReviewScope, WeeklyReview } from "../lib/life-os-contract";

// ── Internal helpers ─────────────────────────────────────────────────────────

/** Parse a local ISO date string into a Date at midnight local time. */
function fromLocal(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

/**
 * Monday of the week containing `today` (ISO-8601: week starts Monday).
 * Returns local ISO date string.
 */
function mondayOf(today: string): string {
  const d = fromLocal(today);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const daysSinceMonday = (dow + 6) % 7; // Mon=0, Tue=1, … Sun=6
  return addDaysIso(-daysSinceMonday, d);
}

/**
 * ISO-8601 week number (Monday-based). Returns 1-53.
 * Algorithm: ISO week 1 = week containing the first Thursday of the year.
 */
function isoWeekNumber(today: string): number {
  const d = fromLocal(today);
  // Nearest Thursday: current date + 3 - daysSinceMonday
  const dayOfWeek = d.getDay(); // 0=Sun
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  const thursday = new Date(d);
  thursday.setDate(d.getDate() - daysSinceMonday + 3);
  // ISO week 1 is the week containing the first Thursday → Jan 4 is always in week 1
  const jan4 = new Date(thursday.getFullYear(), 0, 4);
  const jan4DaysSinceMonday = (jan4.getDay() + 6) % 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - jan4DaysSinceMonday);
  const msPerWeek = 7 * 24 * 3600 * 1000;
  return Math.round((thursday.getTime() - week1Monday.getTime()) / msPerWeek) + 1;
}

/**
 * ISO week year: may differ from calendar year near year boundaries.
 * Uses the Thursday-of-the-week trick (same as isoWeekNumber).
 */
function isoWeekYear(today: string): number {
  const d = fromLocal(today);
  const dow = d.getDay();
  const daysSinceMonday = (dow + 6) % 7;
  const thursday = new Date(d);
  thursday.setDate(d.getDate() - daysSinceMonday + 3);
  return thursday.getFullYear();
}

// ── Exported API ─────────────────────────────────────────────────────────────

/** ISO-8601 week id `YYYY-Www` (Monday-based) for the week containing `today`. */
export function currentWeekId(today?: string): string {
  const date = today ?? (() => {
    const d = new Date();
    return addDaysIso(0, d);
  })();
  const wk = isoWeekNumber(date).toString().padStart(2, "0");
  return `${isoWeekYear(date)}-W${wk}`;
}

/** Month id `YYYY-MM` for `today`. */
export function currentMonthId(today?: string): string {
  const date = today ?? addDaysIso(0);
  return date.slice(0, 7);
}

/** Monday of the current week (local-safe). */
export function weekStartIso(today?: string): string {
  const date = today ?? addDaysIso(0);
  return mondayOf(date);
}

/** Most recent WeeklyReview with the given scope, or null if none. */
export function lastReview(reviews: WeeklyReview[], scope: ReviewScope): WeeklyReview | null {
  const filtered = reviews.filter((r) => r.scope === scope);
  if (filtered.length === 0) return null;
  return filtered.reduce((best, r) => (r.date > best.date ? r : best));
}

/**
 * True when a weekly review is due: no weekly review exists, or the last one's
 * `date` is >7 days before `today`.
 */
export function isWeeklyReviewDue(reviews: WeeklyReview[], today?: string): boolean {
  const date = today ?? addDaysIso(0);
  const last = lastReview(reviews, "week");
  if (!last) return true;
  const daysDiff = Math.round(
    (fromLocal(date).getTime() - fromLocal(last.date).getTime()) / (86_400_000),
  );
  return daysDiff > 7;
}

export type ReviewStats = {
  doneCount: number;
  deferredCount: number;
  habitCompletions: number;
  netCashflow: number;
  activeGoals: number;
  activeProjects: number;
};

/**
 * Shared aggregator. `inWindow(iso)` decides date membership; cashflow is summed
 * from `monthLedger` of each month in `monthKeys` (recurring materialized at real
 * today), then filtered by the same window — so week and month scopes share logic.
 */
function computeStats(
  state: LifeOsState,
  inWindow: (iso: string) => boolean,
  monthKeys: string[],
): ReviewStats {
  let doneCount = 0;
  let deferredCount = 0;
  for (const rev of state.dailyReviews) {
    if (inWindow(rev.date)) {
      doneCount += rev.doneTaskIds.length;
      deferredCount += rev.deferredTaskIds.length;
    }
  }

  let habitCompletions = 0;
  for (const habit of state.habits) {
    for (const c of habit.completions) {
      if (inWindow(c)) habitCompletions++;
    }
  }

  let netCashflow = 0;
  for (const mk of new Set(monthKeys)) {
    for (const e of monthLedger(state, mk)) {
      if (inWindow(e.date)) netCashflow += e.type === "income" ? e.amount : -e.amount;
    }
  }

  const activeGoals = state.goals.filter((g) => g.status === "active").length;
  const activeProjects = state.projects.filter((p) => p.status === "active").length;
  return { doneCount, deferredCount, habitCompletions, netCashflow, activeGoals, activeProjects };
}

/**
 * Aggregate stats for the ISO week starting on `weekStart` (Monday ISO date).
 * Done/deferred (daily reviews), habit completions, and TRUE weekly cashflow are
 * all scoped to the 7-day window [weekStart, weekStart+6]. NO unbounded loops.
 */
export function weekStats(state: LifeOsState, weekStart: string): ReviewStats {
  const weekEnd = addDaysIso(6, fromLocal(weekStart));
  return computeStats(
    state,
    (iso) => iso >= weekStart && iso <= weekEnd,
    [weekStart.slice(0, 7), weekEnd.slice(0, 7)], // a week can span 2 months
  );
}

/** Aggregate stats for the calendar month `monthId` (YYYY-MM). */
export function monthStats(state: LifeOsState, monthId: string): ReviewStats {
  return computeStats(state, (iso) => iso.slice(0, 7) === monthId, [monthId]);
}
